import { Box, Stack, Text } from '@atlaskit/primitives';
import { getDatabaseContainerType, type AnalyticsClient } from '@databases/analytics';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { messages } from '@databases/intl/messages';
import { doDeleteFields } from '@databases/redux/contextual-menu/actions';
import { appStore } from '@databases/redux/store';
import { type SelectionsActions } from '@databases/selections-api-types';
import { getService } from '@databases/service-container';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React from 'react';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import { type CellContextMenuContext } from '../types';

/**
 * Handles the deletion of one or more fields from a database, showing a confirmation dialog to the user.
 * If the user confirms, dispatches the action to delete the selected fields, hides the panel if necessary,
 * and sends analytics events for field deletion and calculation removal.
 */
export function deleteFields({
  contentId,
  formatMessage,
  client,
  showDialog,
  hidePanel,
  fieldId,
  panelFieldId,
  selectionsActions,
}: {
  contentId: string;
  fieldId: string;
  panelFieldId: string;
  formatMessage: MessageFormatter;
  client: AnalyticsClient;
  showDialog: CellContextMenuContext['dialogs']['showDialog'];
  hidePanel: CellContextMenuContext['panelManager']['hidePanel'];
  selectionsActions: SelectionsActions;
}) {
  const fieldNames: string[] = [];
  const reader = getService('SelectionsReader');
  const database = selectDatabaseByContentIdRequired(appStore.getState(), {
    contentId,
  });
  const selectedEntireFieldIds = reader.getSelectedFieldIds();

  selectedEntireFieldIds.forEach((fieldId) => {
    if (database.fieldsById[fieldId]) {
      fieldNames.push(database.fieldsById[fieldId].name);
    }
  });

  showDialog('ConfirmDialog', {
    heading: formatMessage(messages['field-settings.delete-field']),
    body: (
      <Stack space="space.150">
        <Box>
          {formatMessage(
            messages['field-settings.delete-multiple-fields-confirmation-message.fields'],
            {
              fieldsNumber: fieldNames.length,
              fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
            }
          )}
        </Box>
        <Box>
          {formatMessage(
            messages[
              'field-settings.delete-multiple-fields-confirmation-message.additional'
            ]
          )}
        </Box>
      </Stack>
    ),
    yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
    noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
    appearance: 'danger',
    onClose: (result: boolean) => {
      selectionsActions.clearSelectionIntent();

      if (result) {
        const reliability = ReliabilityMetrics.getInstance();

        try {
          const selectedFieldIds =
            getService('SelectionsReader').getFieldIdsFromSelection();

          if (selectedFieldIds.includes(panelFieldId)) {
            hidePanel();
          }

          appStore.dispatch(doDeleteFields(contentId));
          reliability.success(MetricName.DATABASE_DELETE_FIELD);
        } catch (e) {
          const errorMessage = e instanceof Error ? e.message : String(e);
          reliability.failure(MetricName.DATABASE_DELETE_FIELD, {
            errorMessage,
          });
          throw e;
        }
      }
    },
  });

  const databaseContainer = getDatabaseContainerType();

  void client.sendDeleteFieldEvent({
    contentId,
    databaseContainer,
  });

  client.sendRemoveCalculationEvent({
    databaseContainer,
    fieldId,
    fieldType: database.fieldsById[fieldId].type,
    source: 'selectValueMenu',
    contentId: database.contentId,
  });
}
